import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, interval, Subscription} from 'rxjs';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {CompanyService} from './company.service';
import {ErrorHandlerService} from './error-handler.service';
import {catchError, switchMap, takeWhile, map, finalize} from 'rxjs/operators';
import {throwError} from 'rxjs';
import {buildReportOtherFilters} from '../../shared/utils/filter-utils';
import {
  ExportStatus,
  ExportTask,
  ExportPdfRequest,
  ExportPdfResponse,
  ExportStatusResponse
} from '../../shared/models/export-tracking.model';

@Injectable({
  providedIn: 'root'
})
export class ExportTrackingService {
  private readonly baseUrl = environment.baseBackendUrl;
  private readonly STORAGE_KEY = 'mojito-export-tasks';
  private exports$ = new BehaviorSubject<ExportTask[]>([]);
  private pollingSubscriptions = new Map<string, Subscription>();

  constructor(
    private http: HttpClient,
    private companyService: CompanyService,
    private errorHandlerService: ErrorHandlerService
  ) {
    // Cargar exportaciones desde localStorage al inicializar
    this.loadExportsFromStorage();
  }

  /**
   * Observable de todas las exportaciones
   */
  get exports(): Observable<ExportTask[]> {
    return this.exports$.asObservable();
  }

  /**
   * Inicia una nueva exportación PDF
   * @param reportKey Key del reporte (ej: 'crm')
   * @param sectionKey Section key del reporte (ej: 'crm')
   * @param reportName Nombre del reporte para mostrar
   * @param filters Filtros actuales del reporte
   * @param paging
   * @param fileExtension
   * @param detailsId
   */
  startExport(
    reportKey: string,
    sectionKey: string,
    reportName: string,
    filters: {
      dateFrom: string;
      dateTo: string;
      otherFilters?: any[];
      [key: string]: any;
    },
    paging?: { sort: string; tablekey: string },
    fileExtension?: string,
    detailsId?: string,
  ): Observable<ExportTask> {
    const task: ExportTask = {
      id: this.generateTaskId(),
      jobId: 0,
      fileName: this.generateFileName(reportName, fileExtension),
      status: ExportStatus.PENDING,
      createdAt: new Date(),
      reportKey,
      sectionKey,
      reportName,
      filters: {...filters},
      paging,
      fileExtension,
      detailsId
    };

    this.addExport(task);

    const requestBody: ExportPdfRequest = {
      filters: {
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        otherFilters: filters.otherFilters || buildReportOtherFilters(filters)
      },
      ...(paging && {paging}),
      ...(fileExtension && {fileExtension})
    };
    const url = `${this.baseUrl}/api/reports/${reportKey}/${sectionKey}${detailsId ? '/' + detailsId : ''}/job`;

    return this.http.post<ExportPdfResponse>(
      url,
      requestBody,
      {headers: this.buildHeaders()}
    ).pipe(
      map(response => {
        task.jobId = response.jobId;
        task.status = ExportStatus.PROCESSING;
        this.updateExport(task);
        this.startPolling(task.id, reportKey, sectionKey);
        return task;
      }),
      catchError(error => {
        task.status = ExportStatus.FAILED;
        this.updateExport(task);
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Inicia polling para una tarea
   */
  private startPolling(taskId: string, reportKey: string, sectionKey: string): void {
    const task = this.getExport(taskId);
    if (!task) return;

    const subscription = interval(2000)
      .pipe(
        switchMap(() => this.checkExportStatus(reportKey, sectionKey, task.jobId)),
        takeWhile(statusResponse => {
          const status = this.mapStatus(statusResponse.status);
          return status === ExportStatus.PROCESSING || status === ExportStatus.PENDING;
        }, true), // Incluir el último valor que no cumple la condición
        finalize(() => this.stopPolling(taskId))
      )
      .subscribe({
        next: (statusResponse) => {
          const updatedTask = this.getExport(taskId);
          if (!updatedTask) return;

          updatedTask.status = this.mapStatus(statusResponse.status);
          updatedTask.downloadUrl = statusResponse.downloadUrl;

          if (this.isTerminalStatus(updatedTask.status)) {
            updatedTask.completedAt = new Date();
            if (updatedTask.status === ExportStatus.FAILED && !statusResponse.downloadUrl) {
              updatedTask.errorMessage = 'La exportación falló';
            }
          }

          this.updateExport(updatedTask);
        },
        error: () => {
          const task = this.getExport(taskId);
          if (task) {
            task.status = ExportStatus.FAILED;
            task.errorMessage = 'Error al verificar el estado';
            task.completedAt = new Date();
            this.updateExport(task);
          }
        }
      });

    this.pollingSubscriptions.set(taskId, subscription);
  }

  /**
   * Verifica el estado de una exportación
   */
  private checkExportStatus(reportKey: string, sectionKey: string, jobId: number): Observable<ExportStatusResponse> {
    return this.http.get<ExportStatusResponse>(
      `${this.baseUrl}/api/reports/${reportKey}/${sectionKey}/status/${jobId}`,
      {headers: this.buildHeaders()}
    ).pipe(
      catchError(error => {
        this.errorHandlerService.handleError(error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Elimina una exportación de la lista
   */
  removeExport(taskId: string): void {
    this.stopPolling(taskId);
    const current = this.exports$.value;
    const updated = current.filter(t => t.id !== taskId);
    this.exports$.next(updated);
    this.saveExportsToStorage(updated);
  }

  /**
   * Reintenta una exportación fallida
   */
  retryExport(taskId: string): Observable<ExportTask> {
    const task = this.getExport(taskId);
    if (!task?.reportKey || !task.sectionKey || !task.reportName) {
      return throwError(() => new Error('No se puede reintentar esta exportación'));
    }

    this.removeExport(taskId);

    const filters = task.filters || this.getDefaultFilters();
    return this.startExport(task.reportKey, task.sectionKey, task.reportName, filters, task.paging, task.fileExtension, task.detailsId);
  }

  /**
   * Limpia exportaciones completadas o fallidas antiguas
   */
  clearCompletedExports(): void {
    const current = this.exports$.value;
    const active = current.filter(t =>
      t.status === ExportStatus.PROCESSING ||
      t.status === ExportStatus.PENDING
    );
    this.exports$.next(active);
    this.saveExportsToStorage(active);
  }

  /**
   * Carga las exportaciones desde localStorage
   */
  loadExportsFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const tasks = JSON.parse(stored).map((t: any) => ({
          ...t,
          createdAt: new Date(t.createdAt),
          completedAt: t.completedAt ? new Date(t.completedAt) : undefined
        } as ExportTask));

        // Actualizar el BehaviorSubject
        this.exports$.next(tasks);

        // Reiniciar polling para exportaciones en proceso
        tasks.forEach((task: ExportTask) => {
          if (task.status === ExportStatus.PROCESSING || task.status === ExportStatus.PENDING) {
            if (task.reportKey && task.sectionKey && task.jobId > 0) {
              this.startPolling(task.id, task.reportKey, task.sectionKey);
            }
          }
        });
      }
    } catch (error) {
      console.error('Error al cargar exportaciones desde localStorage:', error);
      // Si hay error, limpiar el storage corrupto
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  /**
   * Guarda las exportaciones en localStorage
   */
  private saveExportsToStorage(tasks: ExportTask[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(tasks));
    } catch (error) {
      console.error('Error al guardar exportaciones en localStorage:', error);
    }
  }

  // Métodos privados auxiliares
  private addExport(task: ExportTask): void {
    const current = this.exports$.value;
    const updated = [task, ...current];
    this.exports$.next(updated);
    this.saveExportsToStorage(updated);
  }

  private updateExport(updatedTask: ExportTask): void {
    const current = this.exports$.value;
    const index = current.findIndex(t => t.id === updatedTask.id);
    if (index !== -1) {
      current[index] = {...updatedTask};
      const updated = [...current];
      this.exports$.next(updated);
      this.saveExportsToStorage(updated);
    }
  }

  private getExport(taskId: string): ExportTask | undefined {
    return this.exports$.value.find(t => t.id === taskId);
  }

  private stopPolling(taskId: string): void {
    const subscription = this.pollingSubscriptions.get(taskId);
    if (subscription) {
      subscription.unsubscribe();
      this.pollingSubscriptions.delete(taskId);
    }
  }

  private generateTaskId(): string {
    return `export-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }

  private generateFileName(reportName: string, fileExtension?: string): string {
    const date = new Date().toISOString().split('T')[0];
    const ext = fileExtension || 'pdf';
    return `${reportName}_${date}.${ext}`;
  }

  private mapStatus(backendStatus: string): ExportStatus {
    const statusMap: Record<string, ExportStatus> = {
      'pending': ExportStatus.PENDING,
      'processing': ExportStatus.PROCESSING,
      'completed': ExportStatus.COMPLETED,
      'failed': ExportStatus.FAILED,
      'cancelled': ExportStatus.CANCELLED
    };
    return statusMap[backendStatus.toLowerCase()] || ExportStatus.PENDING;
  }

  private isTerminalStatus(status: ExportStatus): boolean {
    return status === ExportStatus.COMPLETED || status === ExportStatus.FAILED || status === ExportStatus.CANCELLED;
  }

  private getDefaultFilters(): { dateFrom: string; dateTo: string } {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 3);
    return {
      dateFrom: startDate.toISOString(),
      dateTo: endDate.toISOString()
    };
  }

  /**
   * Construye los headers HTTP incluyendo el X-Company-Id
   */
  private buildHeaders(): HttpHeaders {
    const selectedCompany = this.companyService.selectedCompany$.value;
    let headers = new HttpHeaders();

    if (selectedCompany?.id) {
      headers = headers.set('X-Company-Id', selectedCompany.id.toString());
    }

    return headers;
  }
}

